/**
 * Simple in-memory storage implementation for when localStorage/sessionStorage
 * is unavailable (e.g. Safari Private Mode, strict browser settings).
 */
class MemoryStorage implements Storage {
    private _data: Map<string, string>;

    constructor() {
        this._data = new Map();
    }

    get length(): number {
        return this._data.size;
    }

    clear(): void {
        this._data.clear();
    }

    getItem(key: string): string | null {
        return this._data.get(key) || null;
    }

    key(index: number): string | null {
        const keys = Array.from(this._data.keys());
        return keys[index] || null;
    }

    removeItem(key: string): void {
        this._data.delete(key);
    }

    setItem(key: string, value: string): void {
        this._data.set(key, value);
    }

    [name: string]: any;
}

/**
 * Checks if a storage type (localStorage or sessionStorage) is available and working.
 * Some browsers (like Safari in Private mode, or Edge with tracking prevention)
 * might expose the object but throw an error when trying to use it.
 */
export function isStorageAvailable(type: 'localStorage' | 'sessionStorage' = 'localStorage'): boolean {
    try {
        const storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        const val = storage.getItem(x);
        storage.removeItem(x);
        return val === x;
    } catch (e) {
        return false;
    }
}

/**
 * Returns a safe storage instance.
 * If the requested storage is available, returns it.
 * Otherwise, returns an in-memory fallback.
 */
export function getSafeStorage(type: 'localStorage' | 'sessionStorage' = 'localStorage'): Storage {
    if (typeof window === 'undefined') {
        return new MemoryStorage();
    }

    if (isStorageAvailable(type)) {
        return window[type];
    }

    // Fallback to in-memory storage if native storage is blocked
    console.warn(`[Storage] ${type} is not available. Using in-memory fallback. Data will be lost on reload.`);
    if (!(window as any).__memoryStorageInstance) {
        (window as any).__memoryStorageInstance = new MemoryStorage();
    }
    return (window as any).__memoryStorageInstance;
}

/**
 * Safe localStorage wrapper that handles tracking prevention.
 * Use this instead of direct localStorage access.
 */
class SafeLocalStorage {
    private _memory: MemoryStorage = new MemoryStorage();

    private get storage(): Storage {
        try {
            if (typeof window !== 'undefined' && isStorageAvailable('localStorage')) {
                return window.localStorage;
            }
        } catch (e) {
            // fall through to memory
        }
        return this._memory;
    }

    getItem(key: string): string | null {
        try {
            return this.storage.getItem(key);
        } catch (e) {
            return this._memory.getItem(key);
        }
    }

    setItem(key: string, value: string): void {
        try {
            this.storage.setItem(key, value);
        } catch (e) {
            this._memory.setItem(key, value);
        }
    }

    removeItem(key: string): void {
        try {
            this.storage.removeItem(key);
        } catch (e) {
            this._memory.removeItem(key);
        }
    }

    clear(): void {
        try {
            this.storage.clear();
        } catch (e) {
            this._memory.clear();
        }
    }

    key(index: number): string | null {
        try {
            return this.storage.key(index);
        } catch (e) {
            return this._memory.key(index);
        }
    }

    get length(): number {
        try {
            return this.storage.length;
        } catch (e) {
            return this._memory.length;
        }
    }
}

// Export a singleton instance for use throughout the app
export const safeLocalStorage = new SafeLocalStorage();
